import type { FC } from 'react';
import { Typography } from '@interstate/components/Typography';
import type { DealData } from '../types';
import { ChangeSummaryColumn, DataSection, NewDataSection, ChangeGroup, FieldLine, FieldLabel } from '../CreditAppSearch.styles';
import { valueOrDash } from '../utils/resultCardMappers';

type Field = { label: string; value?: string };
type Address = NonNullable<DealData['coBuyer']>['address'];

const renderFields = (fields: Field[]) => (
    <FieldLine>
        {fields.map(({ label, value }) => (
            <Typography key={label} variant="body-sm">
                <FieldLabel>{label}:</FieldLabel> {valueOrDash(value)}
            </Typography>
        ))}
    </FieldLine>
);

const renderAddress = (address?: Address) => (
    <>
        {renderFields([{ label: 'Street', value: address?.street }])}
        {renderFields([
            { label: 'City', value: address?.city },
            { label: 'State', value: address?.state },
            { label: 'ZIP', value: address?.zip }
        ])}
    </>
);

interface ChangeSummaryCardProps {
    title: string;
    description: string;
    dealData: DealData;
    testId: string;
    isNew?: boolean;
}

export const ChangeSummaryCard: FC<ChangeSummaryCardProps> = ({ title, description, dealData, testId, isNew = false }) => {
    const Section = isNew ? NewDataSection : DataSection;
    return (
        <ChangeSummaryColumn data-testid={testId}>
            <div>
                <Typography variant="h5" color={isNew ? 'base.color.green.600' : undefined} data-testid={`${testId}-title`}>
                    {title}
                </Typography>
                <Typography variant="body-sm">{description}</Typography>
            </div>
            <Section>
                <ChangeGroup>
                    <Typography variant="strong-sm">Buyer</Typography>
                    {renderFields([
                        { label: 'First Name', value: dealData.buyer.firstName },
                        { label: 'Middle', value: dealData.buyer.middleName },
                        { label: 'Last Name', value: dealData.buyer.lastName }
                    ])}
                </ChangeGroup>
                <ChangeGroup>
                    <Typography variant="strong-sm">Address</Typography>
                    {renderAddress(dealData.buyer.address)}
                </ChangeGroup>
                <ChangeGroup>
                    <Typography variant="strong-sm">Co-Buyer</Typography>
                    {renderFields([
                        { label: 'First Name', value: dealData.coBuyer?.firstName },
                        { label: 'Last Name', value: dealData.coBuyer?.lastName }
                    ])}
                </ChangeGroup>
                <ChangeGroup>
                    <Typography variant="strong-sm">Co-Buyer Address</Typography>
                    {renderAddress(dealData.coBuyer?.address)}
                </ChangeGroup>
            </Section>
        </ChangeSummaryColumn>
    );
};
